import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Send, CheckCircle, XCircle, Scale,
  MinusCircle, PlusCircle, AlertTriangle, Copy, Edit, Lock,
  RefreshCw, Clock, Ban, User, Calendar, Hash
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdjustmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adj, setAdj] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('einvoice_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fmt = (v) => new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(v || 0);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/einvoice/gateway/adjustments/${id}`, { headers });
        if (!res.ok) throw new Error();
        setAdj(await res.json());
      } catch {
        toast.error('Adjustment not found');
        navigate('/einvoice/portal/adjustment');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const getStatusBadge = (status) => {
    const map = {
      draft: { icon: FileText, cls: 'bg-slate-100 text-slate-600', label: 'DRAFT' },
      submitted: { icon: Send, cls: 'bg-blue-100 text-blue-700', label: 'SUBMITTED' },
      valid: { icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-700', label: 'VALID (LOCKED)' },
      rejected: { icon: XCircle, cls: 'bg-red-100 text-red-700', label: 'REJECTED' },
      error: { icon: AlertTriangle, cls: 'bg-amber-100 text-amber-700', label: 'ERROR' },
      cancelled: { icon: Ban, cls: 'bg-slate-100 text-slate-500', label: 'CANCELLED' },
    };
    const c = map[status] || map.draft;
    const Icon = c.icon;
    return <Badge className={`flex items-center gap-1 px-3 py-1 ${c.cls}`}><Icon className="w-4 h-4" />{c.label}</Badge>;
  };

  const handleSubmit = async () => {
    if (!window.confirm(`Submit ${adj.adjustment_number} to LHDN?`)) return;
    try {
      const res = await fetch(`${API}/api/einvoice/gateway/adjustments/${id}/submit-lhdn`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Submitted to LHDN');
      // Refresh
      const refresh = await fetch(`${API}/api/einvoice/gateway/adjustments/${id}`, { headers });
      if (refresh.ok) setAdj(await refresh.json());
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleClone = async () => {
    try {
      const res = await fetch(`${API}/api/einvoice/gateway/adjustments/${id}/clone`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Cloned as ${data.adjustment_number}`);
      navigate(`/einvoice/portal/adjustment/${data.id}`);
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <PortalLayout><div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div></PortalLayout>;
  if (!adj) return null;

  const isCn = adj.adjustment_type === 'credit_note';
  const isLocked = ['valid', 'submitted'].includes(adj.status);

  return (
    <PortalLayout>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6" data-testid="adjustment-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/einvoice/adjustment')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800 dark:text-gray-100 flex items-center gap-2">
                {isCn ? <MinusCircle className="w-5 h-5 text-red-500" /> : <PlusCircle className="w-5 h-5 text-green-500" />}
                {adj.adjustment_number}
              </h1>
              <p className="text-sm text-slate-500 dark:text-gray-400">
                {isCn ? 'Credit Note' : 'Debit Note'} — e-Invoice Adjustment
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(adj.status)}
            {isLocked && <Lock className="w-4 h-4 text-slate-400" title="Locked — submitted/valid adjustments cannot be edited" />}
          </div>
        </div>

        {/* Rejection banner */}
        {adj.status === 'rejected' && adj.rejection_reason && (
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4" data-testid="rejection-banner">
            <div className="flex items-start gap-2">
              <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-red-700 dark:text-red-400 text-sm">Adjustment Rejected by LHDN</p>
                <p className="text-xs text-red-600 dark:text-red-300 mt-1">{adj.rejection_reason}</p>
                <p className="text-xs text-slate-500 mt-2">Please correct the issue or verify details with your PMS system.</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/einvoice/portal/adjustment/create?edit=${id}`)} data-testid="fix-resubmit-btn">
                    <Edit className="w-3 h-3 mr-1" /> Fix & Resubmit
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleClone} data-testid="clone-btn">
                    <Copy className="w-3 h-3 mr-1" /> Clone Adjustment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Original Invoice */}
          <Card className="dark:bg-[#1a1a2e]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" /> Original Invoice</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <InfoRow label="Invoice No" value={adj.original_invoice_number} mono />
              <InfoRow label="LHDN UUID" value={adj.original_lhdn_uuid} mono small />
              <InfoRow label="Original Amount" value={fmt(adj.original_amount)} bold />
              <InfoRow label="Customer" value={adj.guest_name} />
              {adj.guest_tin && <InfoRow label="Buyer TIN" value={adj.guest_tin} mono />}
              {adj.pms_reference_id && <InfoRow label="PMS Ref" value={adj.pms_reference_id} />}
            </CardContent>
          </Card>

          {/* Adjustment Details */}
          <Card className={`dark:bg-[#1a1a2e] border-l-4 ${isCn ? 'border-l-red-400' : 'border-l-green-400'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Scale className="w-4 h-4 text-purple-500" /> Adjustment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <InfoRow label="Type" value={
                <Badge className={isCn ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                  {isCn ? 'Credit Note (02)' : 'Debit Note (03)'}
                </Badge>
              } />
              <InfoRow label="Adjustment Amount" value={
                <span className={`font-mono font-bold text-lg ${isCn ? 'text-red-600' : 'text-green-600'}`}>
                  {isCn ? '-' : '+'}{fmt(adj.amount)}
                </span>
              } />
              <InfoRow label="Effective Total" value={<span className="font-mono font-bold text-purple-600">{fmt(adj.effective_total)}</span>} />
              {adj.refund_type && <InfoRow label="Refund Type" value={adj.refund_type === 'full' ? 'Full Refund' : 'Partial Refund'} />}
              <InfoRow label="Reason Category" value={adj.reason_category?.replace('_', ' ')} />
              <InfoRow label="Reason" value={adj.reason} />
              {adj.lhdn_uuid && <InfoRow label="Adj LHDN UUID" value={adj.lhdn_uuid} mono small />}
              <InfoRow label="Created" value={adj.created_at ? new Date(adj.created_at).toLocaleString('en-MY') : '-'} />
              {adj.created_by_name && <InfoRow label="Created By" value={adj.created_by_name} />}
            </CardContent>
          </Card>
        </div>

        {/* Financial Impact */}
        <Card className="bg-slate-900 dark:bg-slate-800 text-white" data-testid="financial-impact">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 mb-3 uppercase font-semibold">Financial Impact</p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-300">Original Invoice</span>
              <span className="font-mono">{fmt(adj.original_amount)}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-300">Adjustment</span>
              <span className={`font-mono font-bold ${isCn ? 'text-red-400' : 'text-green-400'}`}>
                {isCn ? '-' : '+'}{fmt(adj.amount)}
              </span>
            </div>
            <hr className="border-slate-700 my-2" />
            <div className="flex items-center justify-between">
              <span className="font-semibold">New Effective Total</span>
              <span className="font-mono text-xl font-bold text-purple-300">{fmt(adj.effective_total)}</span>
            </div>
            <p className={`text-xs mt-2 ${isCn ? 'text-red-400' : 'text-green-400'}`}>
              {isCn ? `This CN reduces revenue by ${fmt(adj.amount)}` : `This DN increases revenue by ${fmt(adj.amount)}`}
            </p>
          </CardContent>
        </Card>

        {/* Line Items */}
        {adj.line_items?.length > 0 && (
          <Card className="dark:bg-[#1a1a2e]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Line Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adj.line_items.map((li, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{li.description}</TableCell>
                      <TableCell className="text-right font-mono">{li.quantity}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(li.unit_price)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(li.tax_amount)}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{fmt(li.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={() => navigate('/einvoice/adjustment')} data-testid="back-to-list">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to List
          </Button>
          <div className="flex gap-2">
            {adj.status === 'draft' && (
              <>
                <Button variant="outline" onClick={() => navigate(`/einvoice/adjustment/create?edit=${id}`)} data-testid="edit-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-btn">
                  <Send className="w-4 h-4 mr-1" /> Submit to LHDN
                </Button>
              </>
            )}
            {adj.status === 'rejected' && (
              <>
                <Button variant="outline" onClick={() => navigate(`/einvoice/portal/adjustment/create?edit=${id}`)} data-testid="fix-btn">
                  <Edit className="w-4 h-4 mr-1" /> Fix & Resubmit
                </Button>
                <Button variant="outline" onClick={handleClone} data-testid="clone-btn">
                  <Copy className="w-4 h-4 mr-1" /> Clone
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

const InfoRow = ({ label, value, mono, bold, small }) => (
  <div className="flex items-start justify-between gap-2">
    <span className="text-slate-500 dark:text-gray-400 text-xs shrink-0">{label}</span>
    <span className={`text-right ${mono ? 'font-mono' : ''} ${bold ? 'font-bold' : ''} ${small ? 'text-xs break-all max-w-[200px]' : ''}`}>
      {value || '-'}
    </span>
  </div>
);

export default AdjustmentDetailPage;
